import confetti from 'canvas-confetti';

/**
 * Shared celebration helpers built on canvas-confetti. Centralised so the
 * homepage easter egg and badge-unlock moment share one look and one place to
 * respect the user's reduced-motion preference.
 */

/** True when the user has asked the OS to minimise motion. */
const prefersReducedMotion = (): boolean =>
	typeof window !== 'undefined' &&
	typeof window.matchMedia === 'function' &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Schedule the next animation frame, falling back to a ~60fps timer in
 * environments where `requestAnimationFrame` is unavailable (tests, non-browser
 * runtimes, older/embedded browsers).
 */
const scheduleFrame = (callback: () => void): void => {
	if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
		window.requestAnimationFrame(callback);
	} else {
		setTimeout(callback, 16);
	}
};

/**
 * Fire a celebratory confetti burst from the bottom-left and bottom-right
 * corners for a short, joyful moment. No-ops when reduced motion is preferred.
 */
export const celebrate = (durationMs = 2000): void => {
	if (prefersReducedMotion()) {
		return;
	}

	const end = Date.now() + durationMs;
	const colors = ['#26a69a', '#7e57c2', '#ffd54f', '#ef5350', '#42a5f5'];

	const frame = () => {
		confetti({
			particleCount: 4,
			angle: 60,
			spread: 55,
			origin: { x: 0, y: 0.9 },
			colors,
			zIndex: 2000
		});
		confetti({
			particleCount: 4,
			angle: 120,
			spread: 55,
			origin: { x: 1, y: 0.9 },
			colors,
			zIndex: 2000
		});
		if (Date.now() < end) {
			scheduleFrame(frame);
		}
	};

	frame();
};

/**
 * A single sharp burst from a point (defaults to centre). Used for the badge
 * unlock moment. No-ops when reduced motion is preferred.
 */
export const burst = (origin: { x: number; y: number } = { x: 0.5, y: 0.4 }): void => {
	if (prefersReducedMotion()) {
		return;
	}

	confetti({
		particleCount: 120,
		spread: 90,
		startVelocity: 45,
		origin,
		colors: ['#26a69a', '#7e57c2', '#ffd54f', '#ef5350', '#42a5f5'],
		zIndex: 2000
	});
};
